import { useEffect, useState } from 'react';
import type { DocumentAuditFinding, DocumentAuditRow } from '@claro/shared';
import { CheckCircle2, FileSearch, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { reviewRequest } from './ManualReviewDialog';

type Source = 'alvaraFuncionamento' | 'avcb' | 'receita';
type Decision = 'consider' | 'disregard';
type Props = {
  row: DocumentAuditRow;
  source?: Source;
  onSaved: () => void | Promise<void>;
  onReview?: (source: Source, field?: string) => void;
};

const sourceNames: Record<Source, string> = {
  alvaraFuncionamento: 'Alvará', avcb: 'AVCB', receita: 'Receita',
};
const fieldNames: Record<string, string> = {
  cnpj: 'CNPJ', razaoSocial: 'Razão social', logradouro: 'Logradouro', numero: 'Número',
  complemento: 'Complemento', bairro: 'Bairro', cep: 'CEP', municipio: 'Município', uf: 'UF',
  inscricaoMunicipal: 'Inscrição municipal', validade: 'Validade', expirationDate: 'Validade',
  cadastro: 'Cadastro', arquivo: 'Arquivo', leitura: 'Leitura',
};
const referenceNames: Record<string, string> = {
  receita: 'Receita', propertyDocs: 'PropertyDocs', document: 'Documento',
};

export function DivergenceDecisions({ row, source, onSaved, onReview }: Props) {
  const [editing, setEditing] = useState<{ id: string; decision: Decision; revision: number; fingerprint: string } | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const findings = (row.assessment.findings ?? []).filter(finding => !source || finding.source === source);
  const disregarded = findings.filter(finding => finding.decision?.decision === 'disregard').length;

  useEffect(() => {
    setEditing(null);
    setReason('');
    setError('');
  }, [row.rowId, source]);

  function begin(finding: DocumentAuditFinding, decision: Decision) {
    setEditing({ id: finding.id, decision, revision: row.reviewRevision ?? 0, fingerprint: finding.fingerprint });
    setReason('');
    setError('');
  }

  async function save(finding: DocumentAuditFinding) {
    if (!editing || busy) return;
    if (!finding.canDeliberate || editing.fingerprint !== finding.fingerprint || editing.revision !== (row.reviewRevision ?? 0)) return;
    if (reason.trim().length < 10) {
      setError('Descreva o motivo da decisão usando pelo menos 10 caracteres.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await reviewRequest('/claro/api/audit/deep/review/decision', {
        method: 'POST',
        body: JSON.stringify({
          rowId: row.rowId,
          issueId: finding.id,
          revision: editing.revision,
          fingerprint: editing.fingerprint,
          decision: editing.decision,
          reason: reason.trim(),
        }),
      });
      await onSaved();
      setEditing(null);
      setReason('');
      toast({
        title: 'Decisão registrada',
        description: 'O painel foi atualizado e a justificativa ficou registrada no histórico.',
      });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!row.assessment.findings) {
    const reasons = row.assessment.reasons.filter(message => !source || message.startsWith(`${sourceNames[source]}:`) || (source === 'receita' && !/^(Alvará|AVCB):/.test(message)));
    return <div className="space-y-2">{reasons.map(message => <p key={message} className="rounded-lg border p-3 text-sm">{message}</p>)}{!reasons.length && <p className="text-sm text-muted-foreground">Sem apontamentos nos critérios documentais avaliados.</p>}</div>;
  }

  return <section aria-label="Deliberação dos apontamentos" className="space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="text-sm font-semibold">Apontamentos{source ? ` · ${sourceNames[source]}` : ''}</h3>
      <p className="text-xs text-muted-foreground">{findings.length - disregarded} ativo(s) · {disregarded} desconsiderado(s)</p>
    </div>
    {findings.length > 0 && <p className="text-xs leading-relaxed text-muted-foreground">Considerar uma divergência de CNPJ coloca o cadastro no vermelho; outras divergências consideradas pesam amarelo. Desconsiderar remove o peso do apontamento. Os valores lidos no documento permanecem disponíveis na comparação.</p>}
    {!findings.length && <p className="flex items-center gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-4 text-sm"><CheckCircle2 className="h-4 w-4 text-emerald-600"/>Sem apontamentos nos critérios documentais avaliados.</p>}
    {findings.map(finding => {
      const ignored = finding.decision?.decision === 'disregard';
      const active = editing?.id === finding.id;
      const stale = active && (editing.fingerprint !== finding.fingerprint || editing.revision !== (row.reviewRevision ?? 0) || !finding.canDeliberate);
      const label = `${sourceNames[finding.source]} · ${fieldNames[finding.field] || 'Conferência documental'}`;
      const inputId = `finding-reason-${finding.id}`;
      return <article key={finding.id} aria-label={label} className={`rounded-lg border p-4 ${ignored ? 'border-slate-300/60 bg-muted/30' : finding.impact === 'critical' ? 'border-red-500/25 bg-red-500/5' : finding.impact === 'attention' ? 'border-amber-500/25 bg-amber-500/5' : 'bg-muted/15'}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold">{label}</h4>
          <span className={`rounded-full px-2 py-1 text-xs font-medium ${ignored ? 'bg-slate-500/10 text-muted-foreground' : 'bg-background/80'}`}>{ignored ? 'Desconsiderado' : finding.decision ? 'Considerado' : finding.canDeliberate ? 'A conferir' : 'Informação pendente'}</span>
        </div>
        <p className="mt-2 text-sm leading-relaxed">{finding.message}</p>
        <p className="mt-1 text-xs text-muted-foreground">Referência: {referenceNames[finding.reference] || finding.reference}{finding.affectsRisk === false ? ' · Comparação adicional' : ''}</p>
        {finding.decision && <div className="mt-3 rounded-md border bg-background/60 p-3 text-xs">
          <p className="font-medium">{finding.decision.email} · {new Date(finding.decision.at).toLocaleString('pt-BR')}</p>
          <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{finding.decision.reason}</p>
        </div>}
        <div className="mt-3 flex flex-wrap gap-2">
          {onReview && <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => onReview(finding.source, finding.field)}><FileSearch className="mr-1 h-4 w-4"/>Detalhes</Button>}
          {finding.canDeliberate && <>
            <Button type="button" size="sm" variant="outline" disabled={busy || finding.decision?.decision === 'consider'} onClick={() => begin(finding, 'consider')}>{ignored && <RotateCcw className="mr-1 h-3 w-3"/>}Considerar</Button>
            <Button type="button" size="sm" variant="outline" disabled={busy || ignored} onClick={() => begin(finding, 'disregard')}>Desconsiderar</Button>
          </>}
        </div>
        {!finding.canDeliberate && <p className="mt-2 text-xs text-muted-foreground">Complete a conferência da fonte para avaliar este apontamento.</p>}
        {active && <form className="mt-4 border-t pt-4" onSubmit={event => { event.preventDefault(); void save(finding); }}>
          <p className="mb-3 text-sm font-medium">{editing.decision === 'disregard' ? 'Desconsiderar este apontamento na avaliação' : 'Considerar este apontamento na avaliação'}</p>
          <label htmlFor={inputId} className="text-sm font-medium">Justificativa da decisão</label>
          <textarea id={inputId} autoFocus maxLength={2000} disabled={busy} value={reason} onChange={event => setReason(event.target.value)} className="mt-2 min-h-24 w-full rounded-md border bg-background p-3 text-sm" placeholder={finding.source === 'avcb' && finding.field === 'cnpj' ? 'Explique a conferência do vínculo do AVCB com este imóvel.' : 'Descreva a evidência e o motivo da sua decisão.'} aria-describedby={`${inputId}-help`}/>
          <p id={`${inputId}-help`} className="text-xs text-muted-foreground">Mínimo de 10 caracteres · {reason.trim().length}/2000</p>
          {stale && <div role="alert" className="mt-3 rounded-md border border-amber-500/30 p-3 text-sm"><p>Os dados foram atualizados durante a conferência. Confira o apontamento atual e reinicie a decisão.</p>{finding.canDeliberate && <Button type="button" size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => begin(finding, editing.decision)}>Reiniciar decisão</Button>}</div>}
          {error && <div role="alert" className="mt-3 rounded-md border border-amber-500/30 p-3 text-sm"><p>{error}</p><Button type="button" size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => { void Promise.resolve(onSaved()).catch(failure => setError(failure instanceof Error ? failure.message : String(failure))); }}>Atualizar apontamentos</Button></div>}
          <div className="mt-3 flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => { setEditing(null); setError(''); }}>Cancelar</Button>
            <Button type="submit" size="sm" disabled={busy || stale}>{busy ? 'Registrando…' : 'Registrar decisão'}</Button>
          </div>
        </form>}
      </article>;
    })}
  </section>;
}
